import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from './wordsApi';
import IconRelation from '../../components/icons/IconRelation';
import IconFileText from '../../components/icons/IconFileText';
import '../../css/modals.css';
import '../../css/course-dependencies.css';

// Same rules as the course delete modal, applied to dictionary words: picking a
// word picks what it cannot exist without (its links and every student's
// progress); progress is never removed unless it is kept selected; a link row
// is only the connection, the lesson/phrase/exercise on the other end stays.
const KIND_LABELS = {
   WORD: 'Слово',
   WSTUDENT: 'Прогрес студента',
   WLESSON: 'Зв\'язок з уроком',
   WPHRASE: 'Зв\'язок з фразою',
   WEXERCISE: 'Зв\'язок з вправою',
};

const KIND_ORDER = ['WORD', 'WSTUDENT', 'WLESSON', 'WPHRASE', 'WEXERCISE'];

const GROUPS = [
   { key: 'words', title: 'Слова' },
   { key: 'progress', title: 'Прогрес студентів' },
   { key: 'links', title: 'Зв\'язки' },
];

const ROLE = {
   WORD: { link: false, text: 'Кінцевий елемент: слово з перекладами, видаляється назавжди. Відповіді бота лишаються, їм тільки знімається посилання на слово.' },
   WSTUDENT: { link: false, text: 'Кінцевий елемент: прогрес студента з цим словом, видаляється назавжди. Без вибору цього рядка слово не видалиться.' },
   WLESSON: { link: true, text: 'Зв\'язок: знімає слово з уроку. Урок лишається.' },
   WPHRASE: { link: true, text: 'Зв\'язок: знімає слово з фрази. Фраза лишається.' },
   WEXERCISE: { link: true, text: 'Зв\'язок: знімає слово з вправи. Вправа лишається.' },
};

const roleOf = (kind) => ROLE[kind] || { link: false, text: 'Кінцевий елемент.' };

const OWNED_LABELS = {
   translations: 'перекладів',
   answers: 'відповідей бота (відв\'яжуться)',
   connectors: 'зв\'язків бота (відв\'яжуться)',
   events: 'подій',
};

const MAX_SKIPPED_SHOWN = 30;
const NO_ITEMS = [];

const kindOf = (key) => key.split(':')[0];

function withRequirements(keys, byKey) {
   const out = new Set();
   const stack = [...keys];
   while (stack.length) {
      const key = stack.pop();
      if (out.has(key)) continue;
      out.add(key);
      const item = byKey[key];
      // Progress is required by its word, but it is never pulled in by a
      // selection - the user has to tick it, so it isn't removed by accident.
      if (item) item.requires.forEach((required) => { if (kindOf(required) !== 'WSTUDENT') stack.push(required); });
   }
   return out;
}

function missingRequires(item, selected) {
   const counts = {};
   item.requires.forEach((required) => {
      if (!selected.has(required)) {
         const kind = kindOf(required);
         counts[kind] = (counts[kind] || 0) + 1;
      }
   });
   return Object.keys(counts).map((kind) => (KIND_LABELS[kind] || kind) + ' (' + counts[kind] + ')');
}

export default function WordDeleteModal({ isOpen, onClose, wordIds, onChanged }) {
   const navigate = useNavigate();
   const [items, setItems] = useState(NO_ITEMS);
   const [loading, setLoading] = useState(false);
   const [loadError, setLoadError] = useState('');
   const [selected, setSelected] = useState(() => new Set());
   const [report, setReport] = useState(null);
   const [busy, setBusy] = useState(false);
   const changedRef = useRef(false);

   const loadItems = () => {
      if (!wordIds || wordIds.length === 0) return;
      setLoading(true);
      setLoadError('');
      return api.getWordDependencies(wordIds)
         .then((response) => {
            setItems(response.data);
            // The words and their links are already chosen by picking the words in
            // the table; progress is not - it shows as a blocker until ticked.
            setSelected(new Set(response.data.filter((item) => item.kind !== 'WSTUDENT').map((item) => item.key)));
         })
         .catch((err) => {
            if (err.response && err.response.status === 401) navigate('/login');
            setLoadError('Не вдалося завантажити залежності слів.');
         })
         .finally(() => setLoading(false));
   };

   useEffect(() => {
      if (!isOpen) return;
      setReport(null);
      loadItems();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, wordIds ? wordIds.join(',') : '']);

   const byKey = useMemo(() => {
      const map = {};
      items.forEach((item) => { map[item.key] = item; });
      return map;
   }, [items]);

   if (!isOpen) return '';

   const groups = GROUPS.map((group) => ({
      ...group,
      items: items.filter((item) => item.group === group.key)
         .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)),
   })).filter((group) => group.items.length > 0);

   const selectedCount = selected.size;
   const wordCount = items.filter((item) => item.kind === 'WORD').length;
   const blockedCount = items.filter((item) => selected.has(item.key)
      && (item.blockers.length > 0 || missingRequires(item, selected).length > 0)).length;

   const close = () => {
      if (changedRef.current) {
         changedRef.current = false;
         if (onChanged) onChanged();
      }
      onClose();
   };

   const toggleItem = (item) => {
      setSelected((prev) => {
         const next = new Set(prev);
         if (next.has(item.key)) {
            next.delete(item.key);
         } else {
            withRequirements([item.key], byKey).forEach((key) => next.add(key));
         }
         return next;
      });
   };

   const toggleGroup = (group) => {
      const keys = group.items.map((item) => item.key);
      const allOn = keys.every((key) => selected.has(key));
      setSelected((prev) => {
         const next = new Set(prev);
         if (allOn) {
            keys.forEach((key) => next.delete(key));
         } else {
            withRequirements(keys, byKey).forEach((key) => next.add(key));
         }
         return next;
      });
   };

   const run = (dryRun) => {
      if (busy || selectedCount === 0) return;
      if (!dryRun && !window.confirm('Видалити вибрані позиції (' + selectedCount + ')? Цю дію не можна скасувати.')) return;
      setBusy(true);
      api.deleteWordDependencies([...selected], dryRun)
         .then((response) => {
            const result = response.data;
            setReport(result);
            if (dryRun) return;
            changedRef.current = true;
            // Everything selected went: nothing to explain, close straight away.
            if (result.skipped.length === 0 && result.deleted.some((k) => k.indexOf('WORD:') === 0)) {
               changedRef.current = false;
               if (onChanged) onChanged();
               onClose();
               return;
            }
            return loadItems();
         })
         .catch((err) => {
            if (err.response && err.response.status === 401) {
               navigate('/login');
               return;
            }
            const text = err.response && typeof err.response.data === 'string'
               ? err.response.data
               : 'Не вдалося виконати запит.';
            setReport({ error: text, deleted: [], skipped: [], counts: {}, dryRun: dryRun });
         })
         .finally(() => setBusy(false));
   };

   const renderItem = (item) => {
      const checked = selected.has(item.key);
      const missing = checked ? missingRequires(item, selected) : [];
      const external = item.blockers;
      const owned = Object.keys(item.owned);
      const blocked = external.length > 0 || missing.length > 0;
      const role = roleOf(item.kind);
      return (
         <label key={item.key} className={'course-deps-item' + (checked ? ' is-selected' : '') + (blocked ? ' is-blocked' : '')}>
            <input type='checkbox' checked={checked} onChange={() => toggleItem(item)} />
            <div className='course-deps-item-main'>
               <div className='course-deps-item-title'>
                  <span className={'course-deps-role ' + (role.link ? 'is-link' : 'is-element')} title={role.text}>
                     {role.link ? <IconRelation /> : <IconFileText />}
                  </span>
                  <span className='course-deps-kind'>{KIND_LABELS[item.kind] || item.kind}</span>
                  <span>{item.label}</span>
                  {item.detail ? <span className='course-deps-muted'>{item.detail}</span> : ''}
               </div>
               {owned.length || external.length || missing.length ? (
                  <div className='course-deps-chips'>
                     {owned.map((code) => (
                        <span key={code} className='course-deps-chip'>{(OWNED_LABELS[code] || code) + ': ' + item.owned[code]}</span>
                     ))}
                     {external.map((text, i) => (
                        <span key={'x' + i} className='course-deps-chip is-blocker'>Заблоковано: {text}</span>
                     ))}
                     {missing.map((text, i) => (
                        <span key={'m' + i} className='course-deps-chip is-blocker'>Потрібно також вибрати: {text}</span>
                     ))}
                  </div>
               ) : ''}
            </div>
         </label>
      );
   };

   const renderReport = () => {
      if (report.error) {
         return <div className='course-deps-report is-error'>{report.error}</div>;
      }
      const tables = Object.keys(report.counts || {}).filter((t) => report.counts[t] > 0);
      return (
         <div className='course-deps-report'>
            <div className='course-deps-report-title'>{report.dryRun ? 'Перевірка: нічого не видалено' : 'Готово'}</div>
            <div>
               {report.dryRun ? 'Буде видалено' : 'Видалено'} позицій: {report.deleted.length}
               {report.skipped.length ? ', пропущено: ' + report.skipped.length : ''}
            </div>
            {!report.dryRun && tables.length ? (
               <div className='course-deps-muted'>Рядків у таблицях: {tables.map((t) => t + ' ' + report.counts[t]).join(', ')}</div>
            ) : ''}
            {report.skipped.slice(0, MAX_SKIPPED_SHOWN).map((s) => (
               <div key={s.key} className='course-deps-skipped'><b>{s.label}</b>: {s.reasons.join('; ')}</div>
            ))}
            {report.skipped.length > MAX_SKIPPED_SHOWN ? (
               <div className='course-deps-muted'>...ще {report.skipped.length - MAX_SKIPPED_SHOWN}</div>
            ) : ''}
         </div>
      );
   };

   return (
      <div className='modal-update' onClick={close} style={{ display: 'flex' }}>
         <div className='modal-window course-deps-modal' onClick={(e) => e.stopPropagation()}>
            <div className='course-deps-head'>
               <h3 className='modal-w-title'>Видалення слів: {wordCount}</h3>
               <div className='course-deps-sub'>
                  Вибір рядка автоматично вибирає все, без чого він не може існувати. Прогрес студентів видаляється
                  остаточно, тому слово можна видалити лише разом з ним. Зв'язки з уроками, фразами і вправами знімаються,
                  самі уроки, фрази і вправи лишаються.
               </div>
            </div>
            <div className='course-deps-toolbar'>
               <button type='button' className='course-deps-link' onClick={() => setSelected(new Set(items.map((i) => i.key)))} disabled={!items.length}>Вибрати все</button>
               <button type='button' className='course-deps-link' onClick={() => setSelected(new Set())} disabled={!selectedCount}>Скасувати вибір</button>
               <span className='course-deps-muted'>Вибрано {selectedCount} з {items.length}</span>
               {blockedCount ? <span className='course-deps-chip is-blocker'>Заблоковано: {blockedCount}</span> : ''}
               <span className='course-deps-legend'>
                  <span className='course-deps-role is-link'><IconRelation /></span> зв'язок
                  <span className='course-deps-role is-element'><IconFileText /></span> кінцевий елемент
               </span>
            </div>
            <div className='course-deps-body'>
               {loading && !items.length ? (
                  <div className='course-deps-muted'>Завантаження...</div>
               ) : loadError ? (
                  <div className='course-deps-error'>{loadError}</div>
               ) : groups.map((group) => (
                  <div key={group.key} className='course-deps-group'>
                     <label className='course-deps-group-head'>
                        <input type='checkbox' checked={group.items.every((item) => selected.has(item.key))} onChange={() => toggleGroup(group)} />
                        <span>{group.title}</span>
                        <span className='course-deps-muted'>{group.items.filter((item) => selected.has(item.key)).length} / {group.items.length}</span>
                     </label>
                     {group.items.map(renderItem)}
                  </div>
               ))}
            </div>
            {report ? renderReport() : ''}
            <div className='course-deps-foot'>
               <button type='button' className='form-button' onClick={close}>Закрити</button>
               <button type='button' className='form-button' disabled={busy || !selectedCount} onClick={() => run(true)}>Перевірити без видалення</button>
               <button type='button' className='course-deps-danger' disabled={busy || !selectedCount} onClick={() => run(false)}>
                  {busy ? 'Видалення...' : 'Видалити вибране (' + selectedCount + ')'}
               </button>
            </div>
         </div>
      </div>
   );
}
